/**
 * Featured Collections Carousel
 * Lightweight, dependency-free carousel using native scroll + CSS scroll-snap.
 * Supports: touch swipe, mouse drag, keyboard nav, infinite loop, autoplay,
 * pagination dots and Shopify theme editor lifecycle events.
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function FeaturedCollectionsCarousel(root) {
    this.root = root;
    this.track = root.querySelector('.fcc-track');
    if (!this.track) return;

    this.cards = Array.prototype.slice.call(this.track.children);
    this.originalCount = this.cards.length;
    if (this.originalCount === 0) return;

    var parsed = {};
    try {
      parsed = JSON.parse(root.dataset.settings || '{}');
    } catch (e) {
      parsed = {};
    }

    this.settings = {
      autoplay: !!parsed.autoplay,
      autoplaySpeed: parseFloat(parsed.autoplaySpeed) || 5,
      infinite: !!parsed.infinite && this.originalCount > 1,
      desktopCards: parseFloat(parsed.desktopCards) || 4,
      tabletCards: parseFloat(parsed.tabletCards) || 2,
      mobileCards: parseFloat(parsed.mobileCards) || 1.2
    };

    this.prevBtn = root.querySelector('.fcc-arrow--prev');
    this.nextBtn = root.querySelector('.fcc-arrow--next');
    this.dotsWrap = root.querySelector('.fcc-dots');
    this.autoplayTimer = null;
    this.isDragging = false;
    this.cloneCount = 0;
    this.allCards = this.cards;

    this.onNext = this.next.bind(this);
    this.onPrev = this.prev.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onInfiniteScroll = debounce(this.handleInfiniteScroll.bind(this), 120);
    this.onBoundedScroll = debounce(this.handleBoundedScroll.bind(this), 120);

    this.init();
  }

  FeaturedCollectionsCarousel.prototype.init = function () {
    if (this.settings.infinite) {
      this.setupInfinite();
    }
    this.buildDots();
    this.bindEvents();
    this.updateArrowState();
    this.updateDots();

    if (this.settings.autoplay) {
      this.startAutoplay();
    }
  };

  FeaturedCollectionsCarousel.prototype.setupInfinite = function () {
    var maxVisible = Math.max(1, Math.ceil(this.settings.desktopCards));
    var cloneCount = Math.min(this.originalCount, maxVisible);
    this.cloneCount = cloneCount;

    var firstClones = this.cards.slice(0, cloneCount).map(function (c) {
      return c.cloneNode(true);
    });
    var lastClones = this.cards.slice(-cloneCount).map(function (c) {
      return c.cloneNode(true);
    });

    var track = this.track;

    lastClones.forEach(function (clone) {
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach(function (a) {
        a.setAttribute('tabindex', '-1');
      });
      track.insertBefore(clone, track.firstChild);
    });

    firstClones.forEach(function (clone) {
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach(function (a) {
        a.setAttribute('tabindex', '-1');
      });
      track.appendChild(clone);
    });

    this.allCards = Array.prototype.slice.call(this.track.children);

    var self = this;
    requestAnimationFrame(function () {
      var prevBehavior = self.track.style.scrollBehavior;
      self.track.style.scrollBehavior = 'auto';
      self.scrollToIndex(self.cloneCount, false);
      self.track.style.scrollBehavior = prevBehavior;
    });
  };

  FeaturedCollectionsCarousel.prototype.getActiveCards = function () {
    return this.allCards;
  };

  FeaturedCollectionsCarousel.prototype.scrollToIndex = function (index, smooth) {
    var cards = this.getActiveCards();
    var card = cards[index];
    if (!card) return;

    var trackRect = this.track.getBoundingClientRect();
    var cardRect = card.getBoundingClientRect();
    var target = cardRect.left - trackRect.left + this.track.scrollLeft;

    this.track.scrollTo({
      left: target,
      behavior: smooth === false ? 'auto' : 'smooth'
    });
  };

  FeaturedCollectionsCarousel.prototype.getCurrentIndex = function () {
    var cards = this.getActiveCards();
    var trackRect = this.track.getBoundingClientRect();
    var closest = 0;
    var minDist = Infinity;

    cards.forEach(function (card, i) {
      var dist = Math.abs(card.getBoundingClientRect().left - trackRect.left);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    return closest;
  };

  FeaturedCollectionsCarousel.prototype.next = function () {
    var cards = this.getActiveCards();
    var idx = this.getCurrentIndex() + 1;

    if (!this.settings.infinite && idx > cards.length - 1) {
      idx = cards.length - 1;
    }

    this.scrollToIndex(idx);
  };

  FeaturedCollectionsCarousel.prototype.prev = function () {
    var idx = this.getCurrentIndex() - 1;
    if (idx < 0) idx = 0;
    this.scrollToIndex(idx);
  };

  FeaturedCollectionsCarousel.prototype.handleInfiniteScroll = function () {
    if (!this.cloneCount) return;

    var idx = this.getCurrentIndex();
    var self = this;

    if (idx < this.cloneCount) {
      var prevBehavior = self.track.style.scrollBehavior;
      self.track.style.scrollBehavior = 'auto';
      self.scrollToIndex(idx + this.originalCount, false);
      self.track.style.scrollBehavior = prevBehavior;
    } else if (idx >= this.cloneCount + this.originalCount) {
      var prevBehavior2 = self.track.style.scrollBehavior;
      self.track.style.scrollBehavior = 'auto';
      self.scrollToIndex(idx - this.originalCount, false);
      self.track.style.scrollBehavior = prevBehavior2;
    }

    this.updateDots();
    this.updateArrowState();
  };

  FeaturedCollectionsCarousel.prototype.handleBoundedScroll = function () {
    this.updateDots();
    this.updateArrowState();
  };

  FeaturedCollectionsCarousel.prototype.updateArrowState = function () {
    if (!this.prevBtn && !this.nextBtn) return;

    if (this.settings.infinite) {
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
      return;
    }

    var idx = this.getCurrentIndex();
    var cards = this.getActiveCards();

    if (this.prevBtn) this.prevBtn.disabled = idx <= 0;
    if (this.nextBtn) this.nextBtn.disabled = idx >= cards.length - 1;
  };

  FeaturedCollectionsCarousel.prototype.buildDots = function () {
    if (!this.dotsWrap) return;

    this.dotsWrap.innerHTML = '';
    this.dots = [];

    var self = this;
    for (var i = 0; i < this.originalCount; i++) {
      (function (index) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fcc-dot';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', 'Go to collection ' + (index + 1));
        btn.addEventListener('click', function () {
          self.scrollToIndex(index + (self.cloneCount || 0));
        });
        self.dotsWrap.appendChild(btn);
        self.dots.push(btn);
      })(i);
    }
  };

  FeaturedCollectionsCarousel.prototype.updateDots = function () {
    if (!this.dots || !this.dots.length) return;

    var idx = this.getCurrentIndex();
    var offset = this.cloneCount || 0;
    var realIndex = ((idx - offset) % this.originalCount + this.originalCount) % this.originalCount;

    this.dots.forEach(function (dot, i) {
      dot.setAttribute('aria-current', i === realIndex ? 'true' : 'false');
    });
  };

  FeaturedCollectionsCarousel.prototype.handleKeydown = function (e) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    }
  };

  FeaturedCollectionsCarousel.prototype.bindEvents = function () {
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);

    this.track.addEventListener('keydown', this.onKeydown);

    if (this.settings.infinite) {
      this.track.addEventListener('scroll', this.onInfiniteScroll, { passive: true });
    } else {
      this.track.addEventListener('scroll', this.onBoundedScroll, { passive: true });
    }

    this.bindDrag();

    if (this.settings.autoplay) {
      var self = this;
      this.root.addEventListener('mouseenter', function () {
        self.stopAutoplay();
      });
      this.root.addEventListener('mouseleave', function () {
        self.startAutoplay();
      });
      this.root.addEventListener('focusin', function () {
        self.stopAutoplay();
      });
      this.root.addEventListener('focusout', function () {
        self.startAutoplay();
      });
    }
  };

FeaturedCollectionsCarousel.prototype.bindDrag = function () {
  var self = this;
  var track = this.track;

  var startX = 0;
  var scrollStart = 0;
  var isPointerDown = false;
  var isDragging = false;
  var dragThreshold = 8;

  function getX(e) {
    return e.touches && e.touches.length
      ? e.touches[0].pageX
      : e.pageX;
  }

  function pointerDown(e) {
    isPointerDown = true;
    isDragging = false;

    startX = getX(e);
    scrollStart = track.scrollLeft;

    self.stopAutoplay();
  }

  function pointerMove(e) {
    if (!isPointerDown) return;

    var x = getX(e);
    var walk = x - startX;

    if (Math.abs(walk) > dragThreshold) {
      isDragging = true;
      self.isDragging = true;
      track.classList.add('is-dragging');
    }

    if (!isDragging) return;

    e.preventDefault();

    track.scrollLeft = scrollStart - walk;
  }

  function pointerUp() {
    isPointerDown = false;

    setTimeout(function () {
      isDragging = false;
      self.isDragging = false;
      track.classList.remove('is-dragging');

      if (self.settings.autoplay) {
        self.startAutoplay();
      }
    }, 0);
  }

  track.addEventListener('mousedown', pointerDown);
  window.addEventListener('mousemove', pointerMove);
  window.addEventListener('mouseup', pointerUp);

  track.addEventListener('touchstart', pointerDown, { passive: true });
  track.addEventListener('touchmove', pointerMove, { passive: false });
  track.addEventListener('touchend', pointerUp);

  // Prevent opening a collection after dragging
  track.addEventListener(
    'click',
    function (e) {
      if (self.isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  track.addEventListener('dragstart', function (e) {
    e.preventDefault();
  });

  this._pointerMove = pointerMove;
  this._pointerUp = pointerUp;
};

  FeaturedCollectionsCarousel.prototype.startAutoplay = function () {
    if (!this.settings.autoplay || this.isDragging) return;
    this.stopAutoplay();
    var self = this;
    this.autoplayTimer = setInterval(function () {
      self.next();
    }, this.settings.autoplaySpeed * 1000);
  };

  FeaturedCollectionsCarousel.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  FeaturedCollectionsCarousel.prototype.destroy = function () {
    this.stopAutoplay();

    if (this._pointerMove) window.removeEventListener('mousemove', this._pointerMove);
    if (this._pointerUp) window.removeEventListener('mouseup', this._pointerUp);
  };

  function initAll(container) {
    var scope = container || document;
    var roots = scope.querySelectorAll ? scope.querySelectorAll('[data-fcc-carousel]') : [];

    roots.forEach(function (el) {
      if (el._fccInstance) return;
      el._fccInstance = new FeaturedCollectionsCarousel(el);
    });
  }

  function destroyIn(container) {
    if (!container || !container.querySelectorAll) return;
    var roots = container.matches && container.matches('[data-fcc-carousel]')
      ? [container]
      : container.querySelectorAll('[data-fcc-carousel]');

    roots.forEach(function (el) {
      if (el._fccInstance) {
        el._fccInstance.destroy();
        el._fccInstance = null;
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    destroyIn(e.target);
  });

  // In case script executes after DOMContentLoaded already fired (defer edge case)
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initAll(document);
  }
})();
